import { RedoOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Card, ConfigProvider, Form, Input, Select } from "antd"
import type React from "react"
import { memo, useEffect } from "react"
import { videoStatuses, type VideoFilters } from "@/services/video/videoApi"

interface VideoSearchFormProps {
  onSearch: (values: Omit<VideoFilters, "page" | "per_page" | "limit">) => void
  loading?: boolean
  initialValues?: Omit<VideoFilters, "page" | "per_page" | "limit">
}

export const VideoSearchForm: React.FC<VideoSearchFormProps> = memo(({ onSearch, loading = false, initialValues }) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues)
    }
  }, [initialValues, form])

  const handleSearch = () => {
    form.validateFields().then((values) => {
      onSearch(values)
    })
  }

  const handleReset = () => {
    form.resetFields()
    onSearch({
      search: "",
      status: undefined,
      is_public: undefined,
    })
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Card size="small">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            search: "",
            status: undefined,
            is_public: undefined,
          }}
          className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 md:grid-cols-4"
        >
          <Form.Item name="search" label="Từ khóa" colon={false} className="mb-0">
            <Input
              allowClear
              autoComplete="off"
              placeholder="Nhập tên video hoặc #ID..."
              prefix={<SearchOutlined className="text-gray-400" />}
              onPressEnter={handleSearch}
            />
          </Form.Item>

          <Form.Item name="status" label="Trạng thái" colon={false} className="mb-0">
            <Select allowClear placeholder="Tất cả trạng thái" onChange={handleSearch} options={videoStatuses} />
          </Form.Item>

          <Form.Item name="is_public" label="Quyền truy cập" colon={false} className="mb-0">
            <Select
              allowClear
              placeholder="Tất cả"
              onChange={handleSearch}
              options={[
                { value: "true", label: "Công khai" },
                { value: "false", label: "Riêng tư" },
              ]}
            />
          </Form.Item>

          <div className="flex items-center gap-2">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
              Tìm kiếm
            </Button>
            <Button icon={<RedoOutlined />} onClick={handleReset} disabled={loading}>
              Đặt lại
            </Button>
          </div>
        </Form>
      </Card>
    </ConfigProvider>
  )
})

VideoSearchForm.displayName = "VideoSearchForm"
